// ADR-0152 (#0748) — lógica pura de la pantalla de avisos del proyecto:
// la fecha programada (día + hora de Santiago → instante), lo que le falta
// al borrador para publicarse y las etiquetas de la lista. Sin DOM ni red.

import {
  ANNOUNCEMENT_BODY_MAX,
  ANNOUNCEMENT_SCHEDULE_MAX_DAYS,
  ANNOUNCEMENT_STATUS_LABEL,
  ANNOUNCEMENT_TITLE_MAX,
  announcementDeliveryLabel,
  type Announcement,
  type AnnouncementCancelReason,
  type AnnouncementAudience,
  type AnnouncementStatus,
} from "@bitacorapro/api-types";
import { addCalendarDays, santiagoDate } from "@bitacorapro/core-engine";

const SANTIAGO_PARTS = new Intl.DateTimeFormat("en-CA", {
  timeZone: "America/Santiago",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

/** La hora de pared de Santiago de un instante: «AAAA-MM-DD» y «HH:MM». */
function santiagoWall(instant: Date): { date: string; time: string } {
  const p = Object.fromEntries(SANTIAGO_PARTS.formatToParts(instant).map((x) => [x.type, x.value]));
  return { date: `${p["year"]}-${p["month"]}-${p["day"]}`, time: `${p["hour"]}:${p["minute"]}` };
}

/**
 * El instante de «tal día a tal hora en Santiago». Prueba los dos desfases
 * de Chile continental (−3 y −4) y se queda con el que da esa hora de
 * pared; null si esa hora no existe ese día (el cambio de hora ocurre a
 * medianoche: las 00:xx de ese día no existen).
 */
export function santiagoInstant(date: string, time: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^\d{2}:\d{2}$/.test(time.slice(0, 5))) return null;
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  const [h, mi] = time.slice(0, 5).split(":").map(Number) as [number, number];
  for (const offsetHours of [3, 4]) {
    const t = new Date(Date.UTC(y, m - 1, d, h + offsetHours, mi));
    const wall = santiagoWall(t);
    if (wall.date === date && wall.time === time.slice(0, 5)) return t;
  }
  return null;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** La ventana del selector de día para programar, en Santiago. El último
 *  día sólo vale hasta la hora de ahora (el servidor mide 90 × 24 h
 *  exactas): `faltaDeAviso` lo revisa con el instante. */
export function ventanaProgramacion(now: Date = new Date()): { min: string; max: string } {
  const min = santiagoDate(now);
  return { min, max: addCalendarDays(min, ANNOUNCEMENT_SCHEDULE_MAX_DAYS) };
}

export type AvisoDraft = {
  title: string;
  body: string;
  /** "" = enviar ahora. */
  date: string;
  time: string;
  programar: boolean;
};

/** Qué le falta al borrador para publicarse; null = listo. El servidor
 *  vuelve a validar todo. */
export function faltaDeAviso(
  d: AvisoDraft,
  ctx: { audience: AnnouncementAudience | null; now?: Date; schedulingAvailable?: boolean }
): string | null {
  const title = d.title.trim();
  const body = d.body.trim();
  if (title.length === 0) return "Escribe un título.";
  if (title.length > ANNOUNCEMENT_TITLE_MAX)
    return `El título tiene más de ${ANNOUNCEMENT_TITLE_MAX} caracteres.`;
  if (body.length === 0) return "Escribe el texto del aviso.";
  if (body.length > ANNOUNCEMENT_BODY_MAX)
    return `El texto tiene más de ${ANNOUNCEMENT_BODY_MAX} caracteres.`;
  if (d.programar) {
    if (ctx.schedulingAvailable === false)
      return "En este ambiente no se pueden programar avisos: envíalo ahora.";
    if (d.date === "" || d.time === "") return "Elige el día y la hora del envío.";
    const at = santiagoInstant(d.date, d.time);
    const now = ctx.now ?? new Date();
    if (at === null) return "Esa hora no existe ese día. Elige otra.";
    if (at.getTime() <= now.getTime()) return "La fecha de envío tiene que estar en el futuro.";
    // El mismo límite que el servidor: el instante, no el día.
    if (at.getTime() > now.getTime() + ANNOUNCEMENT_SCHEDULE_MAX_DAYS * DAY_MS)
      return `Solo se puede programar hasta ${ANNOUNCEMENT_SCHEDULE_MAX_DAYS} días adelante (hasta ${fechaHora(new Date(now.getTime() + ANNOUNCEMENT_SCHEDULE_MAX_DAYS * DAY_MS).toISOString())}).`;
  } else if (ctx.audience && ctx.audience.owners === 0) {
    return "Este proyecto todavía no tiene propietarios con cuenta: no hay a quién avisar.";
  }
  return null;
}

/** «Llegará a 12 propietarios (14 unidades), en la app y por correo.» */
export function textoAudiencia(a: AnnouncementAudience): string {
  const p = a.owners === 1 ? "1 propietario" : `${a.owners} propietarios`;
  const u = a.units === 1 ? "1 unidad" : `${a.units} unidades`;
  return `Llegará a ${p} (${u}), en la app y por correo.`;
}

const FECHA_HORA = new Intl.DateTimeFormat("es-CL", {
  weekday: "short",
  day: "numeric",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
  timeZone: "America/Santiago",
});

/** «jue. 16 oct., 09:00» en Santiago. */
export function fechaHora(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : FECHA_HORA.format(d);
}

export const ESTADO_TONE: Readonly<Record<AnnouncementStatus, string>> = {
  programado: "st-pill--info",
  cancelado: "st-pill--idle",
  enviando: "st-pill--warn",
  enviado: "st-pill--ok",
  retirado: "st-pill--idle",
};

export function estadoLabel(s: AnnouncementStatus): string {
  return ANNOUNCEMENT_STATUS_LABEL[s];
}

/** La columna de envío: cuándo salió o cuándo sale. */
export function envioDe(a: Announcement): string {
  if (a.status === "cancelado") return "No se envió";
  if (a.publishedAt) return fechaHora(a.publishedAt);
  return a.scheduledFor ? `Programado: ${fechaHora(a.scheduledFor)}` : "";
}

/** La columna de entrega: «N de M entregados» y los detalles que pesan. */
export function entregaDe(a: Announcement): { principal: string; detalle: string | null } {
  if (!a.delivery) return { principal: "—", detalle: null };
  const d = a.delivery;
  const partes: string[] = [];
  if (d.suppressed > 0)
    partes.push(
      d.suppressed === 1
        ? "1 eligió no recibir correo"
        : `${d.suppressed} eligieron no recibir correo`
    );
  if (d.noAccount > 0)
    partes.push(
      d.noAccount === 1
        ? "1 ya no tenía cuenta activa"
        : `${d.noAccount} ya no tenían cuenta activa`
    );
  if (d.failed > 0)
    partes.push(d.failed === 1 ? "1 no se pudo enviar" : `${d.failed} no se pudieron enviar`);
  if (d.stopped > 0)
    partes.push(
      d.stopped === 1
        ? "1 no se envió porque se retiró"
        : `${d.stopped} no se enviaron porque se retiró`
    );
  if (d.pending > 0 && a.status !== "retirado")
    partes.push(d.pending === 1 ? "1 por enviar" : `${d.pending} por enviar`);
  return {
    principal: announcementDeliveryLabel(d),
    detalle: partes.length > 0 ? partes.join(" · ") : null,
  };
}

/** Por qué no salió un programado que canceló el sistema. El que canceló
 *  el personal lo cuenta la fila con su nombre. */
const CANCELACION_SISTEMA: Readonly<Record<Exclude<AnnouncementCancelReason, "staff">, string>> = {
  author_not_authorized: "No se envió: quien lo programó ya no publica avisos en este proyecto.",
  no_recipients: "No se envió: a esa hora el proyecto no tenía propietarios con cuenta.",
  overdue: "No se envió: no salió a su hora y se canceló para no avisar tarde.",
};

export function cancelacionDe(a: Announcement): string | null {
  if (a.status !== "cancelado" || a.cancelReason === null || a.cancelReason === "staff")
    return null;
  return CANCELACION_SISTEMA[a.cancelReason];
}
